import { css } from '@emotion/react';
import { IonButton, IonCard, IonCol, IonGrid, IonIcon, IonPopover, IonRow, IonText } from '@ionic/react';
import React from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { GREY_COLOR, UNKNOWN_AQI_LEVEL } from '../../shared/aqi';
import { getDarkerShade } from '../../shared/color';
import * as commonCss from '../../styles/common';
import { LastUpdatedText } from '../LastUpdatedText';
import { SkeletonText } from '../SkeletonText';
import { Title } from '../Title';
import { WidgetPoweredBy } from '../WidgetPoweredBy';
import './AqiCard.override.css';
import { Pill } from './Pill';
import { ellipsisHorizontalCircleOutline } from 'ionicons/icons';
import PollutantTable from '../PollutantTable';
import { isWidgetMode } from '../../shared/widgets-utils';

interface AqiCardProps {
  showDatasourceName?: boolean;
  showPoweredBy?: boolean;
}

export const AqiCard: React.FC<AqiCardProps> = ({ showDatasourceName = false, showPoweredBy = false }) => {
  const {
    aqiStandard,
    aqiLevel: _aqiLevel,
    datasourcePollutantInfos: _datasourcePollutantInfos,
    marker,
  } = useSettings();
  const isLoading = React.useMemo(() => marker === null, [marker]);
  const isWidget = isWidgetMode();

  const aqiLevel = marker?.isStale ? UNKNOWN_AQI_LEVEL : _aqiLevel;
  const mainPollutantInfo = marker?.isStale ? null : _datasourcePollutantInfos?.[0];

  const aqiColor = aqiLevel?.color ?? GREY_COLOR;
  const accentColor = getDarkerShade(aqiColor);
  const backgroundColor = aqiColor;
  const iconHeight = 64;
  const aqiCardCss = {
    card: [
      commonCss.centered,
      css({
        backgroundColor,
        margin: 0,
        padding: 0,
        height: '100%',
        width: '100%',
        display: 'flex',
      }),
    ],
    icon: [css({ fontSize: iconHeight, color: accentColor, marginTop: '.2rem' })],
    timestamp: [
      commonCss.smallText,
      css({
        color: accentColor,
      }),
    ],
    aqiDescriptionText: [
      css({
        maxWidth: '6rem',
        display: 'block',
        // Automatically adjust font size based on how long the description is
        fontSize: `min(1.0rem, ${0.4 + 8 / (aqiLevel?.description.length ?? 8)}rem)`,
        lineHeight: 1.2,
      }),
    ],
    mainPollutant: [
      css({
        color: accentColor,
        fontWeight: 'bold',
      }),
    ],
    mainPollutantRow: [css({ alignItems: 'center', display: 'flex', ['ion-col']: { padding: 0 } })],
    pollutantInfoIcon: [
      css({
        color: accentColor,
      }),
    ],
    mainContent: [commonCss.centered, css({ justifyContent: 'space-around' })],
    popover: [css({ '--width': '400px' })],
  };

  return (
    <IonCard css={aqiCardCss.card}>
      <IonGrid>
        {marker && showDatasourceName && (
          <IonRow>
            <IonCol>
              <Title level={2} color={accentColor}>
                Air Quality at {marker.datasourceName}
              </Title>
            </IonCol>
          </IonRow>
        )}
        <IonRow css={aqiCardCss.mainContent}>
          <IonCol size="auto">
            <IonRow>
              <IonCol css={commonCss.noPadding}>
                {!isLoading ? (
                  <IonIcon src={aqiLevel?.iconUrl} css={aqiCardCss.icon} />
                ) : (
                  <SkeletonText height={iconHeight} width={iconHeight} />
                )}
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol css={commonCss.noPadding}>
                <Title color={accentColor}>
                  <span css={aqiCardCss.aqiDescriptionText}>{aqiLevel?.description ?? 'n/a'}</span>
                </Title>
              </IonCol>
            </IonRow>
          </IonCol>
          <IonCol size="auto">
            <IonRow>
              <IonCol css={commonCss.noPadding}>
                <Pill
                  label={aqiStandard?.name ?? 'n/a'}
                  value={mainPollutantInfo?.metricValue?.toString() ?? 'n/a'}
                  valueColor={accentColor}
                />
              </IonCol>
            </IonRow>
            <IonRow css={aqiCardCss.mainPollutantRow}>
              <IonCol size={!isWidget ? 'auto' : undefined}>
                <IonText css={aqiCardCss.mainPollutant}>
                  Main Pollutant:{' '}
                  {mainPollutantInfo?.aqi != undefined ? (mainPollutantInfo.pollutantDisplayName ?? 'n/a') : 'n/a'}
                </IonText>
              </IonCol>
              {!isWidget && (
                <IonCol>
                  <IonButton size="small" fill="clear" id="btn-pollutant-info">
                    <IonIcon
                      slot="icon-only"
                      css={aqiCardCss.pollutantInfoIcon}
                      icon={ellipsisHorizontalCircleOutline}
                    />
                  </IonButton>
                  <IonPopover trigger="btn-pollutant-info" css={aqiCardCss.popover}>
                    <PollutantTable />
                  </IonPopover>
                </IonCol>
              )}
            </IonRow>
            {aqiLevel?.aqiLevelId !== UNKNOWN_AQI_LEVEL.aqiLevelId && (
              <IonRow>
                <IonCol css={commonCss.noPadding}>
                  <IonText css={aqiCardCss.timestamp}>
                    <LastUpdatedText lastSampledAt={marker?.sampledAt as unknown as string} />
                  </IonText>
                </IonCol>
              </IonRow>
            )}
          </IonCol>
        </IonRow>
        {showPoweredBy && (
          <IonRow>
            <IonCol>
              <WidgetPoweredBy />
            </IonCol>
          </IonRow>
        )}
      </IonGrid>
    </IonCard>
  );
};
